/**
 * Theme Loader Service
 * Main orchestrator for theme loading, caching, and application
 */

import domainResolver from '../lib/domain-resolver';
import themeApiClient from '../lib/theme-api-client';
import cssInjector from '../lib/css-injector';
import themePreloader from '../lib/theme-preloader';
import performanceMonitor from '../utils/performance-monitor';
import * as actionTypes from '../store/actions';

/**
 * ThemeLoaderService class
 * Coordinates domain detection, API calls, caching, and theme application
 */
class ThemeLoaderService {
  constructor() {
    this.store = null;
    this.initialized = false;
    this.currentTheme = null;
  }

  /**
   * Initialize the theme loader with Redux store
   *
   * @param {object} store - Redux store instance
   */
  initialize(store) {
    this.store = store;
    cssInjector.initialize();
    this.initialized = true;
  }

  /**
   * Load theme based on domain detection
   *
   * @param {string} domain - Optional domain override
   * @returns {Promise<object|null>} The loaded branding or null
   */
  async loadTheme(domain = null) {
    if (!this.initialized) {
      console.error('ThemeLoaderService not initialized. Call initialize(store) first.');
      return null;
    }

    try {
      // Detect domain if not provided
      const targetDomain = domain || domainResolver.getCurrentSlug();

      // Dispatch loading action
      this.dispatch({
        type: actionTypes.THEME_LOAD_REQUEST,
        payload: { domain: targetDomain }
      });

      // Fetch branding from API (with cache)
      const branding = await themeApiClient.fetchBranding(targetDomain);

      if (branding) {
        // Apply theme to DOM
        cssInjector.injectTheme(branding);

        // Store current theme
        this.currentTheme = branding;

        // Dispatch success action
        this.dispatch({
          type: actionTypes.THEME_LOAD_SUCCESS,
          payload: {
            branding,
            domain: targetDomain,
            cacheHit: false // TODO: Track cache hits
          }
        });

        return branding;
      } else {
        // No branding found, use defaults
        this.dispatch({
          type: actionTypes.THEME_LOAD_SUCCESS,
          payload: {
            branding: null,
            domain: targetDomain,
            cacheHit: false
          }
        });

        return null;
      }
    } catch (error) {
      console.error('Error loading theme:', error);

      // Dispatch failure action
      this.dispatch({
        type: actionTypes.THEME_LOAD_FAILURE,
        payload: {
          error: error.message || 'Failed to load theme',
          fallbackBranding: this.currentTheme
        }
      });

      return null;
    }
  }

  /**
   * Load theme by organization ID
   *
   * @param {string} organizationId - The organization ID
   * @returns {Promise<object|null>} The loaded branding or null
   */
  async loadThemeById(organizationId) {
    // For now, this uses the same endpoint as domain-based loading
    // In future, could have a separate endpoint for ID-based lookups
    return this.loadTheme(organizationId);
  }

  /**
   * Refresh current theme (bypass cache)
   *
   * @returns {Promise<object|null>} The refreshed branding or null
   */
  async refreshTheme() {
    const currentDomain = domainResolver.getCurrentSlug();

    if (!currentDomain) {
      console.warn('No domain detected for refresh');
      return null;
    }

    try {
      // Invalidate cache
      themeApiClient.invalidateCache(currentDomain);

      // Dispatch invalidate action
      this.dispatch({
        type: actionTypes.THEME_INVALIDATE_CACHE
      });

      // Reload theme
      return this.loadTheme(currentDomain);
    } catch (error) {
      console.error('Error refreshing theme:', error);
      return null;
    }
  }

  /**
   * Clear current theme and reset to defaults
   */
  clearTheme() {
    cssInjector.resetTheme();
    this.currentTheme = null;

    if (this.initialized) {
      this.dispatch({
        type: actionTypes.THEME_RESET
      });
    }
  }

  /**
   * Get current theme
   *
   * @returns {object|null} Current theme branding or null
   */
  getCurrentTheme() {
    return this.currentTheme;
  }

  /**
   * Apply a theme directly (without loading from API)
   *
   * @param {object} branding - The branding data to apply
   * @param {string} domain - The domain slug
   */
  applyTheme(branding, domain) {
    if (!branding) {
      console.warn('No branding data provided');
      return;
    }

    // Apply to DOM
    cssInjector.injectTheme(branding);

    // Store current theme
    this.currentTheme = branding;

    // Update Redux state
    if (this.initialized) {
      this.dispatch({
        type: actionTypes.THEME_LOAD_SUCCESS,
        payload: {
          branding,
          domain,
          cacheHit: true
        }
      });
    }
  }

  /**
   * Initialize theme on app startup
   * Checks cache first, then loads in background
   */
  async initializeOnStartup() {
    const domain = domainResolver.getCurrentSlug();

    if (!domain) {
      console.debug('No domain detected, skipping theme initialization');
      return;
    }

    // Check cache first
    const cached = themeApiClient.cache.get(domain);

    if (cached && cached.branding) {
      // Apply cached theme immediately (no flash)
      cssInjector.injectTheme(cached.branding);
      this.currentTheme = cached.branding;

      if (this.initialized) {
        this.dispatch({
          type: actionTypes.THEME_LOAD_SUCCESS,
          payload: {
            branding: cached.branding,
            domain,
            cacheHit: true
          }
        });
      }
    }

    // Load fresh theme in background
    try {
      const freshBranding = await themeApiClient.fetchBranding(domain);

      // Only update if different from cached version
      if (freshBranding && JSON.stringify(freshBranding) !== JSON.stringify(cached?.branding)) {
        cssInjector.applyThemeWithTransition(freshBranding);
        this.currentTheme = freshBranding;

        if (this.initialized) {
          this.dispatch({
            type: actionTypes.THEME_LOAD_SUCCESS,
            payload: {
              branding: freshBranding,
              domain,
              cacheHit: false
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading fresh theme:', error);
      // Keep cached theme if fresh load fails
    }
  }

  /**
   * Invalidate cache for current domain
   */
  invalidateCache() {
    const domain = domainResolver.getCurrentSlug();
    if (domain) {
      themeApiClient.invalidateCache(domain);

      if (this.initialized) {
        this.dispatch({
          type: actionTypes.THEME_INVALIDATE_CACHE
        });
      }
    }
  }

  /**
   * Clear all cached themes
   */
  clearAllCache() {
    themeApiClient.clearCache();
  }

  /**
   * Get cache information
   *
   * @returns {object} Cache statistics
   */
  getCacheInfo() {
    return themeApiClient.getCacheInfo();
  }

  /**
   * Dispatch Redux action
   *
   * @param {object} action - Redux action
   */
  dispatch(action) {
    if (this.store) {
      this.store.dispatch(action);
    }
  }

  /**
   * Check if theme loader is initialized
   *
   * @returns {boolean} True if initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Preload themes for common organizations
   *
   * @param {Array<string>} domains - Array of domain slugs to preload
   */
  preloadThemes(domains) {
    themePreloader.preloadMultiple(domains);
  }

  /**
   * Get performance metrics
   *
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    return {
      apiMetrics: performanceMonitor.getAllMetrics(),
      cacheInfo: this.getCacheInfo(),
      preloadStats: themePreloader.getPreloadStats()
    };
  }

  /**
   * Log performance metrics to console
   */
  logPerformanceMetrics() {
    const metrics = this.getPerformanceMetrics();
    console.log('[Theme Loader] Performance Metrics:', metrics);
  }
}

// Export singleton instance
export default new ThemeLoaderService();

// Export class for custom instances
export { ThemeLoaderService };
